import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';

import type { AuditSearch } from '@/app/routes/audit';
import { AdminLayout } from '@/app/shell/admin-layout';
import { type AdminAction, type ListAuditQuery, useAuditActions, useImpersonationSessions } from '@/shared/api/admin-audit';
import { fmtAgo, fmtDateTime } from '@/shared/lib/format';
import { useDebounced } from '@/shared/lib/use-debounced';
import {
  Badge,
  Button,
  CopyId,
  FilterChip,
  PageHeader,
  Pagination,
  SearchInput,
  SelectFilter,
  TableShell,
  TableStateRow,
  TD,
  TH,
  THEAD,
  TR,
  WIDE,
} from '@/shared/ui/kit';

const PAGE_SIZE = 100;
const TARGETS = ['user', 'team', 'subscription', 'upgrade_request', 'plan', 'coupon', 'flag', 'managed_key', 'email', 'client_error'];
const DAYS = ['1', '7', '30', '90'] as const;

export function AuditLogPage() {
  const search = useSearch({ from: '/audit' });
  const navigate = useNavigate({ from: '/audit' });
  const setSearch = (patch: Partial<AuditSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, offset: undefined, ...patch }), replace: true });
  const tab = search.tab ?? 'actions';
  const { data: imps } = useImpersonationSessions();

  return (
    <AdminLayout>
      <PageHeader
        title="Audit log"
        description="Every operator action, who did it and why. Kept forever. Detail-page views are hidden unless you ask for them."
        actions={
          <div className="flex gap-1.5">
            <FilterChip label="Actions" active={tab === 'actions'} onClick={() => setSearch({ tab: undefined })} />
            <FilterChip
              label={`Impersonations${imps ? ` (${imps.total})` : ''}`}
              active={tab === 'impersonations'}
              onClick={() => setSearch({ tab: 'impersonations' })}
            />
          </div>
        }
      />
      {tab === 'impersonations' ? <ImpersonationsTable /> : <ActionsTable search={search} setSearch={setSearch} />}
    </AdminLayout>
  );
}

function ActionsTable(props: { search: AuditSearch; setSearch: (p: Partial<AuditSearch>) => void }) {
  const { search, setSearch } = props;
  const [text, setText] = useState(search.kind ?? '');
  const debounced = useDebounced(text.trim(), 300);
  useEffect(() => {
    if ((search.kind ?? '') !== debounced) setSearch({ kind: debounced || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);
  const [open, setOpen] = useState<string | null>(null);

  const offset = search.offset ?? 0;
  // Stable lower bound per chosen window (recomputing on every render would
  // change the query key and refetch in a loop).
  const from = useMemo(
    () => (search.days ? new Date(Date.now() - Number(search.days) * 86_400_000).toISOString() : undefined),
    [search.days],
  );
  const query: ListAuditQuery = { limit: PAGE_SIZE, offset, hideReads: search.reads !== 'show' };
  // Prefix match: "user.b" finds user.ban; "subscription." finds every subscription action.
  if (search.kind) query.actionKind = `${search.kind}*`;
  if (search.target) query.targetType = search.target;
  if (search.targetId) query.targetId = search.targetId;
  if (search.actor) query.actorUserId = search.actor;
  if (from) query.from = from;

  const { data, isLoading, isFetching, error } = useAuditActions(query);
  const scoped = search.targetId || search.actor;

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <SearchInput value={text} onChange={setText} placeholder="Action, e.g. user.ban or subscription." />
        <SelectFilter label="Target" value={search.target} options={TARGETS} onChange={(v) => setSearch({ target: v })} />
        <SelectFilter
          label="Since"
          value={search.days}
          options={DAYS}
          allLabel="any time"
          onChange={(v) => setSearch({ days: v as AuditSearch['days'] })}
        />
        <FilterChip
          label="Show page views"
          active={search.reads === 'show'}
          onClick={() => setSearch({ reads: search.reads === 'show' ? undefined : 'show' })}
        />
        {scoped ? (
          <Button tone="ghost" onClick={() => setSearch({ targetId: undefined, actor: undefined })} className="text-xs">
            Clear {search.targetId ? 'target' : 'operator'} filter ×
          </Button>
        ) : null}
        <div className="ml-auto text-xs text-neutral-500" aria-live="polite">
          {isFetching ? 'Loading…' : data ? `${data.total.toLocaleString()} actions` : null}
        </div>
      </div>

      <TableShell>
        <thead className={THEAD}>
          <tr>
            <th className={`${TH} w-6`} aria-label="Expand" />
            <th className={TH}>When</th>
            <th className={TH}>Action</th>
            <th className={`${TH} ${WIDE}`}>Operator</th>
            <th className={`${TH} ${WIDE}`}>Target</th>
            <th className={`${TH} ${WIDE}`}>Reason</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-800">
          <TableStateRow
            colSpan={6}
            loading={isLoading && !data}
            error={!data ? error : undefined}
            empty={Boolean(data && data.items.length === 0)}
            emptyText="No actions match these filters."
          />
          {data?.items.map((a) => (
            <Fragment key={a.id}>
              <ActionRow
                a={a}
                open={open === a.id}
                onToggle={() => setOpen(open === a.id ? null : a.id)}
                onActor={() => setSearch({ actor: a.actorUserId })}
              />
              {open === a.id ? <ActionDetail a={a} /> : null}
            </Fragment>
          ))}
        </tbody>
      </TableShell>
      {data ? (
        <Pagination total={data.total} offset={offset} pageSize={PAGE_SIZE} onChange={(o) => setSearch({ offset: o || undefined })} />
      ) : null}
    </>
  );
}

function ActionRow(props: { a: AdminAction; open: boolean; onToggle: () => void; onActor: () => void }) {
  const a = props.a;
  return (
    <tr className={TR}>
      <td className={TD}>
        <button
          type="button"
          aria-expanded={props.open}
          aria-label={props.open ? 'Hide details' : 'Show details'}
          onClick={props.onToggle}
          className="rounded p-0.5 text-neutral-500 hover:bg-neutral-800 hover:text-neutral-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
        >
          {props.open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      </td>
      <td className={`${TD} whitespace-nowrap text-neutral-400`} title={fmtDateTime(a.occurredAt)}>
        {fmtAgo(a.occurredAt)}
      </td>
      <td className={TD}>
        <code className="rounded bg-neutral-800 px-1.5 py-0.5 text-xs text-orange-400">{a.actionKind}</code>
        {a.impersonationActorId ? (
          <div className="mt-1">
            <Badge tone="danger">via impersonation</Badge>
          </div>
        ) : null}
      </td>
      <td className={`${TD} ${WIDE}`}>
        <button type="button" onClick={props.onActor} className="text-left text-xs text-neutral-300 hover:text-orange-300" title="Only this operator">
          {a.actorEmail ?? `${a.actorUserId.slice(0, 8)}…`}
        </button>
      </td>
      <td className={`${TD} ${WIDE}`}>
        <TargetCell type={a.targetType} id={a.targetId} />
      </td>
      <td className={`${TD} ${WIDE} max-w-sm text-neutral-400`}>
        <span className="line-clamp-2" title={a.reason ?? ''}>
          {a.reason ?? <span className="text-neutral-600">—</span>}
        </span>
      </td>
    </tr>
  );
}

function TargetCell({ type, id }: { type: string; id: string | null }) {
  return (
    <div className="text-xs">
      <div className="text-neutral-500">{type}</div>
      {id && type === 'user' ? (
        <Link to="/users/$id" params={{ id }} className="font-mono text-neutral-300 hover:text-orange-300">
          {id.slice(0, 8)}…
        </Link>
      ) : id && type === 'team' ? (
        <Link to="/teams/$id" params={{ id }} className="font-mono text-neutral-300 hover:text-orange-300">
          {id.slice(0, 8)}…
        </Link>
      ) : id ? (
        <CopyId value={id} short className="px-0" />
      ) : null}
    </div>
  );
}

function ActionDetail({ a }: { a: AdminAction }) {
  return (
    <tr className="bg-neutral-950/60">
      <td colSpan={6} className="px-4 py-3">
        <dl className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
          <div><dt className="inline text-neutral-500">When: </dt><dd className="inline text-neutral-300">{fmtDateTime(a.occurredAt)}</dd></div>
          <div><dt className="inline text-neutral-500">Operator: </dt><dd className="inline text-neutral-300">{a.actorEmail ?? '—'} <CopyId value={a.actorUserId} short /></dd></div>
          <div><dt className="inline text-neutral-500">Target: </dt><dd className="inline text-neutral-300">{a.targetType} {a.targetId ? <CopyId value={a.targetId} /> : null}</dd></div>
          <div><dt className="inline text-neutral-500">IP: </dt><dd className="inline text-neutral-300">{a.ipAddress ?? '—'}</dd></div>
          {a.reason ? <div className="sm:col-span-2"><dt className="inline text-neutral-500">Reason: </dt><dd className="inline text-neutral-300">{a.reason}</dd></div> : null}
        </dl>
        {Object.keys(a.metadata ?? {}).length > 0 ? (
          <pre className="mt-2 max-h-64 overflow-auto rounded bg-neutral-950 p-3 text-[11px] text-neutral-300 ring-1 ring-neutral-800">
            {JSON.stringify(a.metadata, null, 2)}
          </pre>
        ) : null}
      </td>
    </tr>
  );
}

function ImpersonationsTable() {
  const { data, isLoading, error } = useImpersonationSessions();
  return (
    <TableShell>
      <thead className={THEAD}>
        <tr>
          <th className={TH}>Started</th>
          <th className={TH}>Operator</th>
          <th className={TH}>As user</th>
          <th className={`${TH} ${WIDE}`}>Reason</th>
          <th className={TH}>Status</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-neutral-800">
        <TableStateRow
          colSpan={5}
          loading={isLoading}
          error={error}
          empty={Boolean(data && data.items.length === 0)}
          emptyText="No impersonation sessions yet."
        />
        {data?.items.map((s) => (
          <tr key={s.id} className={TR}>
            <td className={`${TD} whitespace-nowrap text-neutral-400`} title={fmtDateTime(s.startedAt)}>{fmtAgo(s.startedAt)}</td>
            <td className={TD}>
              <Link to="/users/$id" params={{ id: s.actorUserId }} className="font-mono text-xs text-neutral-300 hover:text-orange-300">
                {s.actorUserId.slice(0, 8)}…
              </Link>
            </td>
            <td className={TD}>
              <Link to="/users/$id" params={{ id: s.impersonatedUserId }} className="font-mono text-xs text-neutral-300 hover:text-orange-300">
                {s.impersonatedUserId.slice(0, 8)}…
              </Link>
            </td>
            <td className={`${TD} ${WIDE} max-w-md text-neutral-400`}><span className="line-clamp-2" title={s.reason}>{s.reason}</span></td>
            <td className={TD}>
              {s.endedAt ? (
                <span className="text-xs text-neutral-500">ended ({s.endedReason})</span>
              ) : new Date(s.expiresAt) > new Date() ? (
                <Badge tone="ok">active</Badge>
              ) : (
                <span className="text-xs text-neutral-500">expired</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </TableShell>
  );
}
